import {QueryClient} from '@tanstack/react-query'

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            networkMode: 'online',
            retry: 1,
            retryOnMount: true,
            retryDelay: 1000,
            staleTime: 600000, //10 min
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true
        }
    }
})

export const QUERY_KEYS = {
    ABSENCES: 'absences',
    ACCESS_PROFILES: 'access-profiles',
    AGENDA_AVAILABILITIES: 'agenda-availabilities',
    AGENDA_EVENTS: 'agenda-events',
    AGENDA_EVENTS_COUNT: 'agenda-events-count',
    AGENDA_VIEW: 'agenda-view',
    AGENDA_VIEWS: 'agenda-views',
    ALL_ACTIVITY_LOGS: 'all-activity-logs',
    ASL_BY_CITY: 'asl-by-city',
    ASL_BY_COORDINATES: 'asl-by-coordinates',
    ATTACHMENT_SIGNED_URL: 'attachment-signed-url',
    CITIES: 'cities',
    COMPANY_INFO: 'company-info',
    CONVERSATIONS: 'conversations',
    CONVERSATION_MESSAGES: 'conversation-messages',
    COUNTRIES: 'countries',
    DISEASES: 'diseases',
    DISTRICTS: 'districts',
    DIAGNOSES: 'diagnoses',
    ENTITY_ACTIVITY_LOGS: 'entity-activity-logs',
    EQUIPE_BY_PROJECT: 'equipe-by-project',
    EXTERNAL_COSTS: 'external-costs',
    HOLIDAYS: 'holidays',
    HOME: 'home',
    MEDICAL_REPORT_DATES_BY_PATIENT: 'medical-report-dates-by-patient',
    MEDICAL_REPORT_TYPES_BY_PATIENT: 'medical-report-types-by-patient',
    MEDICAL_REPORT_TYPES: 'medical-report-types',
    MEDICAL_REPORTS: 'medical-reports',
    MUNICIPALITIES: 'municipalities',
    NEUROMUSCULAR_DIAGNOSIS: 'neuromuscular_diagnosis',
    NOTIFICATIONS: 'notifications',
    NOTIFICATION_COUNT: 'notification-count',
    PATIENT_MEDICAL_RECORD: 'patient-medical-record',
    PATIENT_PROJECTS: 'patient-projects',
    PATIENT_EVENTS: 'patient-events',
    PATIENTS: 'patients',
    PERMISSIONS: 'permissions',
    PLANNED_ACCESSES: 'planned-accesses',
    PLANNED_ACCESSES_DETAILS: 'planned-accesses-details',
    PROJECT_ACCESS_COUNTERS: 'project-access-counters',
    PROJECT_BUDGETS: 'project-budgets',
    PROJECTS_BY_PATIENT: 'projects-by-patient',
    PROJECT_EVALUATION_SCALE: 'project-evaluation-scale',
    PROJECT_SOCIAL_WELFARE_CONDITIONS: 'project-social-welfare-conditions',
    PROJECT: 'project-details',
    PROJECT_NOTES: 'project-notes',
    PROJECT_OTHER_PATHOLOGIES: 'project-other-pathologies',
    PROJECT_PATHOLOGIES: 'project-pathologies',
    PROJECT_MEDICAL_DEVICES: 'project-medical-devices',
    PROJECTS: 'project',
    PROJECT_CLINICAL_PRESENTATIONS: 'project-clinical-presentations',
    PROVINCES: 'provinces',
    ROLE_PERMISSIONS: 'role-permissions',
    ACCESS_TYPES: 'access-types',
    ACCESSES: 'accesses',
    ACCESSES_DETAIL: 'accesses-detail',
    ACCESS: 'access',
    SPECIALISTS: 'specialists',
    TREATMENT_REQUESTS_GROUPS: 'treatment-requests-groups',
    TREATMENT_REQUESTS: 'treatment-requests',
    TREATMENT_TYPE_BY_USER: 'treatment-type-by-user',
    USER_ACCESS_TYPES: 'user-access-types',
    USER_AVAILABILITY: 'user-availability',
    USER_GROUPS: 'user-groups',
    USER_SPECIALIZATIONS: 'user-specializations',
    USER_WORK_AREAS: 'user-work-areas',
    USERS_BY_PATIENT: 'users-by-patient',
    USERS: 'users',
    USER_BY_GROUP: 'user-by-group',
    TEAM_MEMBERS: 'team-members',
    VIEW: 'view',
    VIEWS: 'views',
    ACCESS_RECOVERY: 'access-recovery',
    ACCESS_RATES: 'access-rates',
    ACCESSES_DASHBOARD: 'accesses-dashboard',
    ACCESSES_SIGNATURE_SHEETS: 'accesses-signature-sheets',
    DASHBOARD_ANALYTICS: 'dashboard-analytics',
    DASHBOARD_OPS: 'dashboard-ops',
    DASHBOARD_CONSULTANTS: 'dashboard-consultants',
    PROJECT_VALIDATORS: 'project-validators',
    PROJECT_EXTENSION_REQUEST_DATA: 'project-extension-request-data',
    MEDICAL_RECORD: 'medical-record',
    REFERRING_SPECIALISTS: 'referring-specialists',
    MAILER_PREVIEW: 'mailer-preview',
    MAILER_LIVE_COUNT: 'mailer-live-count',
    SIAR_BULK_EXPORT_HISTORY: 'siar-bulk-export-history'
} as const

export const MUTATION_KEYS = {
    CREATE_USER_GROUP: 'create-user-group',
    UPDATE_USER_GROUP: 'update-user-group',
    UPDATE_ACCESS: 'update-access',
    CHANGE_ACCOUNT_EMAIL: 'change-account-status',
    CHANGE_ACCOUNT_PASSWORD: 'change-account-password',
    CHANGE_PATIENT_STATUS: 'change-patient-status',
    CREATE_EVENT: 'create-event',
    CREATE_FOLLOW_UP: 'create-follow-up',
    CREATE_MEDICAL_RECORD: 'create-medical-record',
    CREATE_MEDICAL_REPORT: 'create-medical-report',
    CREATE_PATIENT: 'create-patient',
    CREATE_PLANNED_ACCESSES: 'create-planned-accesses',
    CREATE_PROJECT_MAIN_PATHOLOGIES: 'create-project-main-pathologies',
    CREATE_PROJECT: 'create-project',
    CREATE_SPECIALIST: 'create-specialist',
    CREATE_TREATMENT_REQUEST: 'create-treatment-request',
    CREATE_USER: 'create-user',
    DELETE_DOCUMENT: 'delete-document',
    DELETE_EVENT: 'delete-event',
    DELETE_FOLLOW_UP: 'delete-follow-up',
    DELETE_MEDICAL_REPORT: 'delete-medical-report',
    DELETE_PATIENT: 'delete-patient',
    DELETE_PROJECT: 'delete-project',
    DELETE_PROJECT_SUSPENSION: 'delete-project-suspension',
    DELETE_TREATMENT_REQUEST: 'delete-treatment-request',
    EDIT_EVENT: 'edit-event',
    EDIT_PLANNED_ACCESSES: 'edit-planned-accesses',
    EDIT_PROJECT_DETAILS: 'edit-project-details',
    EXPORT_SIAR: 'export-siar',
    EXPORT_ACCESSES_TXT: 'export-accesses-txt',
    GENERATE_MEDICAL_RECORD_COVER: 'generate-medical-record-cover',
    GENERATE_MEDICAL_REPORT_PDF: 'generate-medical-report-pdf',
    GET_MEDICAL_RECORD_AGGREGATED_PRINTS: 'get-medical-record-aggregated-prints',
    LOGIN: 'login',
    MARK_NOTIFICATION: 'mark-notification',
    MARK_CONVERSATION_AS_READ: 'mark-conversation-as-read',
    PASSWORD_RECOVERY: 'password-recovery',
    PRINT_FM22: 'print-fm22',
    PRINT_SIAR: 'print-siar',
    RENEW_TRANSFORM_PROJECT: 'renew-transform-project',
    RESET_PASSWORD: 'reset-password',
    SET_PASSWORD: 'set-password',
    STORE_TEAM: 'store-team',
    UPDATE_FOLLOW_UP: 'update-follow-up',
    UPDATE_MEDICAL_REPORT: 'update-medical-report',
    UPDATE_PATIENT: 'update-patient',
    UPDATE_TREATMENT_REQUEST: 'update-treatment-request',
    UPDATE_USER_AVAILABILITY: 'update-user-availability',
    UPDATE_USER: 'update-user',
    UPLOAD_DOCUMENT: 'upload-document',
    VERIFY_EMAIL_TOKEN: 'verify-email-token',
    VERIFY_INVITE_TOKEN: 'verify-invite-token',
    VERIFY_RECOVERY_TOKEN: 'verify-recovery-token',
    VALIDATE_ACCESS: 'validate-access',
    ACCOUNT_ACCESS: 'account-access',
    SEND_MAIL: 'send-mail',
    PREVIEW_RECIPIENTS: 'preview-recipients',
    BULK_SIAR_EXPORT: 'bulk-siar-export',
    CONFIRM_SIAR_EXPORT_TRANSMISSION: 'confirm-siar-export-transmission',
    CANCEL_SIAR_EXPORT: 'cancel-siar-export'
} as const
